import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { ActivityIndicator, Image, Platform, View } from 'react-native'
import { Button, Text, useTheme } from 'react-native-paper'
import QRCode from 'qrcode'
import * as Sentry from '@sentry/react-native'
import Toast from 'react-native-root-toast'
import { WechatPaymentPNG } from '../../../utils/meldd-icons'
import { PaymentIcon } from './payment-channel.modal'
import {
  useCreateWechatPayOrderMutation,
  WechatPayMethod,
} from '../../../generated/graphql'
import { MelddModal } from '../../../@ui/containers/modals'
import { useWechatJSSDK } from '../../../contexts/wechat-jssdk.provider'
import { buildH5SceneInfo, getWechatPayMethod } from './wechat-pay.utils'
import {
  isPaymentChannelEnabled,
  PaymentType,
} from './payment-visibility.registry'

type WechatChannelProps = {
  billingId: string
  description: string
  onProcess: () => void
}

/**
 * WeChat Pay channel component
 *
 * Supports three payment methods auto-detected from the environment:
 * - JSAPI: WeChat in-app browser — triggers native WeChat Pay sheet
 * - H5: Mobile browser — redirects to WeChat Pay H5 page
 * - NATIVE: Desktop browser — shows QR code for scanning
 *
 * Flow:
 * 1. User clicks WeChat Pay icon
 * 2. Order is created on the backend
 * 3. Payment is initiated based on method
 * 4. onProcess() is called to show PaymentConfirmationModal for polling
 */
export const WechatChannel = ({
  billingId,
  description,
  onProcess,
}: WechatChannelProps) => {
  const { t: tTiers } = useTranslation('tiers')
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [showQrModal, setShowQrModal] = useState(false)
  const [createWechatPayOrder] = useCreateWechatPayOrderMutation()
  const { initialize, wxInstance } = useWechatJSSDK()

  if (!isPaymentChannelEnabled(PaymentType.WECHAT)) {
    return null
  }

  if (Platform.OS !== 'web') {
    return null
  }

  const handleQrModalClose = () => {
    setShowQrModal(false)
    onProcess()
  }

  const handleWechatPayClick = async () => {
    setLoading(true)
    try {
      const paymentMethod = getWechatPayMethod()

      const input: any = {
        description,
        customId: billingId,
        paymentMethod,
      }

      if (paymentMethod === WechatPayMethod.H5) {
        input.sceneInfo = buildH5SceneInfo()
        input.returnUrl = window.location.href
      }

      const { data } = await createWechatPayOrder({ variables: { input } })

      if (!data?.createWechatPayOrder) {
        throw new Error('Failed to create WeChat Pay order')
      }

      const order = data.createWechatPayOrder
      setLoading(false)

      if (paymentMethod === WechatPayMethod.Jsapi) {
        await handleJSAPI(order)
      } else if (paymentMethod === WechatPayMethod.H5) {
        handleH5(order.h5Url)
      } else {
        await handleNative(order.codeUrl)
      }
    } catch (error: any) {
      console.error('WeChat Pay error:', error)
      Sentry.captureException(error)
      const message =
        error.graphQLErrors?.[0]?.message ||
        error.message ||
        tTiers('payment.wechat-error')
      Toast.show(message, { position: Toast.positions.CENTER })
      setLoading(false)
    }
  }

  const handleJSAPI = async (order: any) => {
    const params = order.paymentParameters
    if (!params) {
      Toast.show(tTiers('payment.wechat-error'), {
        position: Toast.positions.CENTER,
      })
      return
    }

    try {
      const instance =
        wxInstance || (await initialize(window.location.href, ['chooseWXPay']))
      if (!instance) {
        throw new Error('WeChat JSSDK not initialized')
      }

      await new Promise<void>((resolve, reject) => {
        instance.wx.chooseWXPay({
          appId: params.appId,
          timestamp: params.timeStamp,
          nonceStr: params.nonceStr,
          package: params.package,
          signType: params.signType,
          paySign: params.paySign,
          success: () => resolve(),
          fail: (err: any) =>
            reject(new Error(err?.errMsg || 'JSAPI pay failed')),
        })
      })

      onProcess()
    } catch (error: any) {
      console.error('WeChat JSAPI pay error:', error)
      Sentry.captureException(error)
      Toast.show(error.message || tTiers('payment.wechat-error'), {
        position: Toast.positions.CENTER,
      })
    }
  }

  const handleH5 = (h5Url?: string | null) => {
    if (!h5Url) {
      Toast.show(tTiers('payment.wechat-error'), {
        position: Toast.positions.CENTER,
      })
      return
    }
    window.location.href = h5Url
  }

  const handleNative = async (codeUrl?: string | null) => {
    if (!codeUrl) {
      Toast.show(tTiers('payment.wechat-error'), {
        position: Toast.positions.CENTER,
      })
      return
    }

    try {
      const dataUrl = await QRCode.toDataURL(codeUrl, { width: 256, margin: 2 })
      setQrDataUrl(dataUrl)
      setShowQrModal(true)
    } catch (error) {
      console.error('QR code generation failed:', error)
      Sentry.captureException(error)
      Toast.show(tTiers('payment.wechat-error'), {
        position: Toast.positions.CENTER,
      })
    }
  }

  return (
    <>
      <PaymentIcon
        label={tTiers('payment.wechat')}
        source={
          <Image source={WechatPaymentPNG} style={{ width: 32, height: 32 }} />
        }
        loading={loading}
        onPress={handleWechatPayClick}
      />

      <MelddModal
        visible={showQrModal}
        onDismiss={handleQrModalClose}
        title={tTiers('payment.wechat-scan-qr')}
        body={
          <View
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              minHeight: 300,
            }}
          >
            {qrDataUrl ? (
              <>
                <Image
                  source={{ uri: qrDataUrl }}
                  style={{ width: 256, height: 256 }}
                  accessibilityLabel={tTiers('payment.wechat-qr-label')}
                />
                <Text
                  variant="bodyMedium"
                  style={{ marginTop: 16, textAlign: 'center' }}
                >
                  {tTiers('payment.wechat-scan-instruction')}
                </Text>
              </>
            ) : (
              <ActivityIndicator size="large" color={theme.colors.primary} />
            )}
          </View>
        }
        actions={
          <Button onPress={handleQrModalClose}>
            {tTiers('payment.close')}
          </Button>
        }
      />
    </>
  )
}
